import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'

// Open modals, oldest first. Escape closes only the top one, so a preview
// opened from inside Edit artwork does not take that dialog down with it.
const openStack: symbol[] = []

export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 'max-w-lg',
}: {
  open: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
  footer?: ReactNode
  width?: string
}) {
  // A ref, so a parent passing a new onClose each render does not re-run the
  // effect below and move an older modal back to the top of the stack.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    if (!open) return
    const id = Symbol('modal')
    openStack.push(id)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openStack[openStack.length - 1] === id) onCloseRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      openStack.splice(openStack.indexOf(id), 1)
    }
  }, [open])

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-foreground/50 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose()
          }}
        >
          <motion.div
            // max-h + internal scroll keep the panel usable on short viewports
            // (landscape phones), where it would otherwise overflow both edges
            // with the close button out of reach.
            className={cn(
              'flex max-h-[calc(100dvh-2rem)] w-full flex-col rounded-2xl border border-foreground/10 bg-background shadow-xl',
              width,
            )}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
          >
            <div className="flex items-center justify-between border-b border-foreground/10 px-5 py-4">
              <h3 className="text-base font-medium text-foreground">{title}</h3>
              <button
                onClick={onClose}
                aria-label="Close"
                className="-m-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-foreground/5 hover:text-ink"
              >
                <X className="size-5" />
              </button>
            </div>
            <div className="min-h-0 overflow-y-auto px-5 py-4">{children}</div>
            {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
